import React, { useState, useEffect, useRef } from "react";
import {
  BigPrice,
  Button,
  Image,
  InformationalModal,
  tryUseImgix,
  useSplit,
} from "@leafly-com/frontend-components";

import { trackEvent, trackMenuEvent, Category, Action } from "utils/trackEvent";
import { VariantTile } from "components/Dispensary/VariantTile";
import { centsToDollars, formatCurrency } from "common/currencyFormatter";
import { ActionButton } from "./ActionButton";
import getVariant from "api/requests/getVariant";
import { MenuItem, MenuItemVariant } from "custom-types/MenuItem";
import { CartData, getCart } from "api/requests/getReservation";
import { addReservation } from "api/requests/addReservation";
import { NewBagModal } from "components/Dispensary/MenuItemPage/NewBagModal";
import { DispensaryInformation } from "components/Dispensary/MenuItemPage/DispensaryInformation";
import MenuTypeButton from "components/Dispensary/MenuItemPage/MenuTypeButton";
import { finalPrice, quantityLabel } from "./AddToBagModal/formatters";
import { MenuType, menuTypeErrorMessage } from "common/menuTypes";
import { MenuTypeBagResetModal } from "components/Dispensary/MenuItemPage/MenuTypeBagResetModal";
import MenuTypeDisplay from "components/Dispensary/MenuItemPage/MenuTypeDisplay";
import { getMenuItemUrl } from "utils/menuItemUrl";

type AddToBagModalProps = {
  /**
   * cartId is an required prop, which should be used in order to insert a cart
   * ID that is managed elsewhere in application state (e.g. a redux store).
   */
  cartId: string;
  countryCode: string;
  variantId: number;
  onCartFetch?: (cart: CartData) => unknown;
  onDismissed?: () => void;
  returnFocusRef?: React.RefObject<any>;
};

const testVariantsForMenuType = (desiredMenuType: MenuType) => (
  variant: MenuItemVariant
) => variant.medical === (desiredMenuType === MenuType.Med);

/**
 * AddToBagModal displays the complete workflow for adding a menu item to the
 * bag. It should depend on as little app context as possible, as the goal is
 * to eventually move this into a shared component.
 */
export const AddToBagModal: React.FC<AddToBagModalProps> = ({
  cartId: staticCartId,
  countryCode,
  variantId,
  onCartFetch = () => {},
  ...props
}) => {
  const [menuItem, setMenuItem] = useState<MenuItem | null>(null);
  const [
    purchasedVariant,
    setPurchasedVariant,
  ] = useState<MenuItemVariant | null>(null);
  const [staleBag, setStaleBag] = useState<CartData | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState(variantId);
  const [addingToBag, setAddingToBag] = useState(false);
  const [menuType, setMenuType] = useState<MenuType>(MenuType.Rec);
  const [showDispenaryBagResetModal, setShowDispenaryBagResetModal] = useState(
    false
  );
  const [showMenuTypeBagResetModal, setShowMenuTypeBagResetModal] = useState(
    false
  );
  const [pendingBagItem, setPendingBagItem] = useState<MenuItemVariant | null>(
    null
  );
  const [addToBagError, setAddToBagError] = useState(false);

  const addToBagButtonRef = useRef(null);
  const split = useSplit();
  const quantity = 1;
  const track: (event: string) => void = split
    ? split.dispatch.track
    : () => {};

  useEffect(() => {
    getVariant(variantId)
      .then((response: MenuItem) => {
        const variants = response.variants || [];
        const initialVariant = variants.find(
          (variant) => variant.id === selectedVariantId
        );
        const selectingMedicalVariant = initialVariant?.medical || false;

        setMenuItem(response);
        setMenuType(selectingMedicalVariant ? MenuType.Med : MenuType.Rec);
      })
      .catch((e) => console.error(e?.message));
  }, [variantId]);

  const requiresNewBag = staleBag !== null;

  if (!menuItem || !menuItem.variants) {
    return null;
  }

  const hasDualLicense =
    menuItem.dispensary.tags.includes("dualLicense") || false;
  const hasAlternateMenuTypeVariants = menuItem.variants.some((variant) =>
    menuType === MenuType.Rec ? variant.medical : !variant.medical
  );

  const variantsForMenu =
    hasDualLicense && hasAlternateMenuTypeVariants
      ? menuItem.variants.filter(testVariantsForMenuType(menuType))
      : menuItem.variants;

  const sortedVariants = variantsForMenu
    .slice()
    .sort(
      (a, b) =>
        (!!b.deal?.active as any) - (!!a.deal?.active as any) ||
        a.price - b.price
    );

  const hideBagResetModals = () => {
    setShowDispenaryBagResetModal(false);
    setShowMenuTypeBagResetModal(false);
  };

  const onResetModalDismissed = () => {
    hideBagResetModals();
    setStaleBag(null);
  };

  const onAddToBagClick = async () => {
    hideBagResetModals();
    setAddingToBag(true);

    const variant = menuItem.variants!.find((v) => v.id === selectedVariantId);

    if (!variant) {
      return;
    }

    await addReservation({
      cartId: staticCartId,
      forceUpdate: requiresNewBag,
      menuItemId: menuItem.menuItemId,
      quantity,
      variant,
    })
      .then(() => {
        setStaleBag(null);
        setPurchasedVariant(variant);

        // Track a successful add to bag action
        trackMenuEvent("Menu Card ATB Modal", "Add to Bag", menuItem.id);
        track("webDispensaryPdp_addToBagClick_frontend");

        // Signal to cephalopod to refresh the cart
        document.dispatchEvent(new CustomEvent("getcart"));
      })
      .catch(async (error) => {
        /**
         * If adding a cart item returns a 422 response, there is a cart but
         * it is not in an approriate state to accept this item for some reason.
         */
        const errorCode = String(error).split(" ")[0];
        /**
         * It's now also safe to request the cart here, which isn't possible
         * before we attempt to add a cart item, unless also checking for a
         * 404 response. In this case, we're "asking for forgiveness" and not
         * preemptively inspecting the cart before this error handler.
         */
        const cartResponse = await getCart(staticCartId, countryCode);

        setStaleBag(cartResponse);

        switch (errorCode) {
          case "cart_mismatch_error":
            setShowDispenaryBagResetModal(true);
            break;
          case menuTypeErrorMessage:
            setPendingBagItem(variant);
            setShowMenuTypeBagResetModal(true);
            break;
          default:
            setAddToBagError(true);
            trackEvent(
              Category.addToBag,
              Action.impression,
              "ATB Failed Modal"
            );
            console.error(error);
            break;
        }

        if (cartResponse) {
          onCartFetch(cartResponse);
        }
      });

    setAddingToBag(false);
  };

  const onMenuTypeChange = (medical: boolean) => {
    const menuType = medical ? MenuType.Med : MenuType.Rec;
    const variants = menuItem.variants || [];
    /**
     * At least one variant of this menu type must be present in order for this
     * callback to be invoked by the MenuTypeButton.
     */
    const variant = variants.find(testVariantsForMenuType(menuType))!;

    setSelectedVariantId(variant.id);

    setMenuType(menuType);
  };

  const title = purchasedVariant ? "ADDED TO BAG" : "CONFIRM ORDER";
  const menuItemPath = getMenuItemUrl(menuItem, selectedVariantId || variantId);

  return (
    <InformationalModal
      title={title}
      aria-labelledby="add-to-bag-label"
      {...props}
    >
      {({ dismiss }: { dismiss: () => void }) => (
        <>
          {addToBagError ? (
            <>
              <div className="p-sm border border-light-grey rounded">
                <div className="p-xs bg-yellow-20 border border-yellow-40 mb-sm text-xs">
                  {`Oh no! Item ${menuItem.name} by ${menuItem.brandName} is no longer for
                  pickup and has been removed.`}
                </div>
                <MenuItemHeader menuItem={menuItem} showLink={false} />
              </div>

              <div className="mt-sm">
                {staleBag ? (
                  <>
                    <div className="mb-md">{`No worries, you still have ${
                      staleBag.cartItems.length
                    } other product${
                      staleBag.cartItems.length > 1 ? "s" : ""
                    } in your bag.`}</div>
                    <ActionButton
                      variant="secondary"
                      className="mb-xs"
                      href="/pickup"
                    >
                      check out
                    </ActionButton>
                    <ActionButton
                      variant="tertiary"
                      icon="bag"
                      className="border-none"
                      onClick={dismiss}
                    >
                      keep shopping
                    </ActionButton>
                  </>
                ) : (
                  <ActionButton
                    variant="tertiary"
                    icon="bag"
                    className="flex-grow-0"
                    onClick={dismiss}
                  >
                    keep shopping
                  </ActionButton>
                )}
              </div>

              <DispensaryInformation dispensary={menuItem.dispensary} />

              <div className="text-xs mt-lg">
                You may have one bag going at a time. Select{" "}
                <button
                  onClick={dismiss}
                  onKeyPress={dismiss}
                  className="text-green cursor-pointer"
                >
                  keep shopping
                </button>{" "}
                to add more items from same dispensary.
              </div>
            </>
          ) : purchasedVariant ? (
            <>
              <MenuItemHeader menuItem={menuItem} href={menuItemPath}>
                <div className="mt-md font-bold">
                  {quantityLabel(purchasedVariant, quantity)}
                </div>
                {purchasedVariant.deal && purchasedVariant.deal.active && (
                  <div className="text-notification font-bold">
                    {
                      formatCurrency(
                        centsToDollars(
                          purchasedVariant.deal.totalSavings * quantity
                        ),
                        countryCode
                      )?.formattedPrice
                    }{" "}
                    savings
                  </div>
                )}
                <div className="mt-md font-bold">
                  <div className="mb-xs">Total Price</div>
                  <BigPrice
                    {...formatCurrency(
                      finalPrice(purchasedVariant, quantity),
                      countryCode
                    )}
                  />
                </div>
              </MenuItemHeader>

              <div className="flex mt-sm -mx-xs">
                <ActionButton
                  variant="tertiary"
                  icon="bag"
                  className="flex-grow-0"
                  onClick={dismiss}
                >
                  keep shopping
                </ActionButton>

                <ActionButton
                  variant="secondary"
                  className="flex-grow-0"
                  href="/pickup"
                >
                  check out
                </ActionButton>
              </div>

              <DispensaryInformation dispensary={menuItem.dispensary} />

              <div className="text-xs mt-lg">
                You may have one bag going at a time. Select{" "}
                <button
                  onClick={dismiss}
                  onKeyPress={dismiss}
                  className="text-green cursor-pointer"
                >
                  keep shopping
                </button>{" "}
                to add more items from same dispensary.
              </div>
            </>
          ) : (
            <>
              {hasDualLicense && (
                <div className="flex flex-row-reverse pb-lg">
                  {hasAlternateMenuTypeVariants ? (
                    <MenuTypeButton
                      handleMenuTypeIsMedicalChange={onMenuTypeChange}
                      color="white"
                      medical={menuType === MenuType.Med}
                    />
                  ) : (
                    <MenuTypeDisplay medical={menuType === MenuType.Med} />
                  )}
                </div>
              )}
              <MenuItemHeader menuItem={menuItem} href={menuItemPath} />
              <div className="pb-sm">Choose your amount</div>
              {/*
                The scrolling container, with negative margins on the small
                breakpoint to align to the edges of the viewport.
                Flexbox is required on this element for the child's padding
                to be applied correctly.
              */}
              <div
                className={`
                  -mb-sm flex
                  -mx-lg md:mx-none
                  pl-lg md:pl-none
                  overflow-x-scroll md:overflow-visible
                `}
              >
                {/*
                  Add the flexbox setup preventing wrap on mobile, as
                  well as the right hand padding to allow the final tile
                  to be aligned to the right side when scrolled to the end.
                  Some margins are applied using JSX in order to only apply
                  the ".row" negative margin above or at the medium breakpoint.
                */}
                <div
                  className={`
                    flex md:flex-grow items-stretch -mx-xs
                    flex-no-wrap md:flex-wrap
                    pr-md md:pr-none
                  `}
                >
                  {sortedVariants.map((variant) => (
                    /*
                      Padding on the x-axis is used here instead of margin
                      so that the column helper can override the mobile
                      value on desktop sized breakpoints.
                    */
                    <div
                      className="px-xs mb-sm flex-grow-0 md:col-1/2"
                      key={variant.id}
                    >
                      <VariantTile
                        countryCode={countryCode}
                        onClick={() =>
                          setSelectedVariantId(variant.id as number)
                        }
                        onKeyDown={(e: any) =>
                          e.code === "Enter" &&
                          setSelectedVariantId(variant.id as number)
                        }
                        selected={variant.id === selectedVariantId}
                        className="cursor-pointer"
                        variant={variant}
                        /**
                         * When there is only one variant, we can skip adding
                         * the `tabindex` property, as the only action to take
                         * is to add the one available variant to the bag.
                         */
                        {...(sortedVariants.length > 1 ? { tabIndex: 0 } : {})}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <Button
                variant="primary"
                className="mt-xl flex justify-center items-center"
                onClick={(e: any) => {
                  onAddToBagClick();
                  e.preventDefault();
                }}
                onKeyDown={(e: any) => {
                  e.code === "Enter" && onAddToBagClick();
                  e.preventDefault();
                }}
                ref={addToBagButtonRef}
                disabled={addingToBag}
              >
                {addingToBag ? (
                  "adding…"
                ) : (
                  <>
                    <svg height="20" width="20" className="mr-sm">
                      <use href="#bag" xlinkHref="#bag"></use>
                    </svg>
                    add to bag
                  </>
                )}
              </Button>
              <DispensaryInformation dispensary={menuItem.dispensary} />
            </>
          )}

          {showDispenaryBagResetModal && (
            <NewBagModal
              newDispensary={menuItem.dispensary}
              onDismissed={onResetModalDismissed}
              onKeepBagClick={dismiss}
              onNewBagClick={onAddToBagClick}
              returnFocusRef={addToBagButtonRef}
            />
          )}

          {pendingBagItem && showMenuTypeBagResetModal && (
            <MenuTypeBagResetModal
              medical={pendingBagItem.medical}
              onDismissed={onResetModalDismissed}
              onNewBagClick={onAddToBagClick}
              returnFocusRef={addToBagButtonRef}
            />
          )}
        </>
      )}
    </InformationalModal>
  );
};

const MenuItemHeader: React.FC<{
  menuItem: any;
  href?: string;
  showLink?: boolean;
}> = ({ menuItem, href, showLink = true, children }) => {
  const menuItemImage = tryUseImgix(
    menuItem &&
      (menuItem.thumbnailImageUrl ||
        menuItem.imageUrl ||
        menuItem.fallbackImage)
  );

  return (
    <div className="mb-xl flex">
      <div className="image-container flex-shrink-0">
        <Image
          className="object-contain flex-shrink-0"
          src={menuItemImage}
          alt={menuItem.name}
          sizes={[70]}
        />
      </div>

      <div className="ml-sm">
        {showLink && href ? (
          <a className="text-sm font-bold text-green block" href={href}>
            {menuItem.name}
          </a>
        ) : (
          <span className="text-sm font-bold block">{menuItem.name}</span>
        )}
        {children}
      </div>

      <style jsx>{`
        .image-container {
          max-width: 70px;
        }
      `}</style>
    </div>
  );
};
